import { useEffect, useState } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { applyGymContext } from '@/lib/applyGymContext';
import { normalizeJoinCode } from '@/lib/gymJoinCode';
import { markOnboardingFlag } from '@/lib/onboardingProgress';
import { getClubMemberLimit } from '@/lib/subscription';

const log = createLogger('JOIN_GYM');

type GymLookup = {
  id: string;
  name?: string | null;
  address?: string | null;
  place_id?: string | null;
  google_place_id?: string | null;
  lat?: number | null;
  lng?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  join_code?: string | null;
};

export default function JoinGymScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ code?: string | string[]; source?: string | string[] }>();
  const { user, refreshProfile, profile } = useAuth();
  const { theme } = useTheme();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!params?.code) return;
    if (code) return;
    const incomingCode = Array.isArray(params.code) ? params.code[0] : params.code;
    if (!incomingCode) return;
    setCode(String(incomingCode).replace(/\s+/g, '').toUpperCase());
  }, [params?.code, code]);

  const resolveGymByCode = async (normalizedCode: string): Promise<GymLookup | null> => {
    log.info('[JOIN_GYM] submitted code', normalizedCode);
    log.info('[JOIN_GYM] join_code lookup skipped (column does not exist)');

    // MVP fallback: match against the gym_id prefix (pilot only).
    const { data: idData, error: idError } = await supabase
      .from('gyms')
      .select('id, name')
      .limit(500);

    if (idError) {
      log.info('[JOIN_GYM] id-prefix lookup error', idError);
      throw idError;
    }

    const matches = (idData || []).filter((gym: GymLookup) =>
      gym.id.toUpperCase().startsWith(normalizedCode)
    );

    if (matches.length > 1) {
      log.warn('[JOIN_GYM] id-prefix lookup returned multiple gyms', {
        count: matches.length,
        ids: matches.map((gym) => gym.id),
      });
    }

    const firstMatch = matches.length > 0 ? matches[0] : null;
    log.info('[JOIN_GYM] id-prefix lookup found?', firstMatch?.id || null);
    return firstMatch;
  };

  const handleJoin = async () => {
    const normalizedCode = normalizeJoinCode(code);
    if (!normalizedCode) {
      setError('Enter a join code to continue.');
      return;
    }
    if (!user?.id) {
      setError('You must be signed in to join a gym.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const gym = await resolveGymByCode(normalizedCode);
      if (!gym) {
        setError('Invalid code. Ask your gym for the 6-character code from their poster.');
        setLoading(false);
        return;
      }

      // ── Member limit enforcement ──────────────────────────────
      // Count current members of this gym
      const { count: memberCount, error: countError } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .eq('home_gym_id', gym.id);

      if (!countError && memberCount != null) {
        // Look up the gym owner's subscription_plan_id to enforce member limits.
        // The plan_id is stored on the owner's profile via the Stripe webhook.
        const { data: gymRecord } = await supabase
          .from('gyms')
          .select('owner_id')
          .eq('id', gym.id)
          .maybeSingle();

        let planId: string | null = null;
        if (gymRecord?.owner_id) {
          const { data: ownerProfile } = await supabase
            .from('profiles')
            .select('subscription_plan_id')
            .eq('id', gymRecord.owner_id)
            .maybeSingle();
          planId = ownerProfile?.subscription_plan_id ?? null;
        }

        const limit = getClubMemberLimit(planId);
        if (memberCount >= limit) {
          setError("This gym has reached its member limit. Ask the owner to upgrade their plan.");
          setLoading(false);
          return;
        }
      }
      // ─────────────────────────────────────────────────────────

      await applyGymContext(user.id, gym);
      if (refreshProfile) await refreshProfile();
      const needsGate =
        profile?.onboarding_completed !== true ||
        !profile?.handle?.trim() ||
        !profile?.display_name?.trim();
      const sourceParam = Array.isArray(params?.source) ? params.source[0] : params?.source;
      const isOnboarding = sourceParam === 'onboarding' || needsGate;

      if (isOnboarding) {
        if (user?.id) {
          await markOnboardingFlag(user.id, 'seenGymStep');
        }
        router.replace({
          pathname: '/(onboarding)/gym-joined',
          params: { gymName: gym.name || 'your gym', gymId: gym.id, mode: 'onboarding' },
        });
      }
      else {
        router.replace({
          pathname: '/(onboarding)/gym-joined',
          params: { gymName: gym.name || 'your gym', gymId: gym.id, mode: 'normal' },
        });
      }
    } catch (err: any) {
      log.info('[JOIN_GYM] join failed', err);
      setError('Could not join gym right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={{ width: 24 }} />
        <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>JOIN A GYM</Text>
        <View style={{ width: 24 }} />
      </View>

      <View style={styles.content}>
        <Text style={[styles.title, { color: theme.colors.text }]}>Join a Gym</Text>
        <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
          Enter the code your gym provided.
        </Text>

        {error ? (
          <Text
            style={[
              styles.errorText,
              { color: theme.colors.accent, borderColor: theme.colors.accent },
            ]}>
            {error}
          </Text>
        ) : null}

        <TextInput
          style={[
            styles.input,
            {
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
              color: theme.colors.text,
            },
          ]}
          value={code}
          onChangeText={(value) => setCode(normalizeJoinCode(value))}
          placeholder="CODE"
          placeholderTextColor={theme.colors.textTertiary}
          autoCapitalize="characters"
          autoCorrect={false}
          editable={!loading}
        />

        <TouchableOpacity
          style={[styles.joinButton, { backgroundColor: theme.colors.accent }]}
          onPress={handleJoin}
          disabled={loading}>
          {loading ? (
            <ActivityIndicator color={theme.colors.accentForeground} />
          ) : (
            <Text style={[styles.joinButtonText, { color: theme.colors.accentForeground }]}>
              Join
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 1,
    textAlign: 'center',
  },
  content: {
    padding: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: '800',
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 20,
  },
  input: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    letterSpacing: 2,
    marginBottom: 16,
  },
  joinButton: {
    paddingVertical: 14,
    borderRadius: 2,
    alignItems: 'center',
  },
  joinButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  errorText: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 10,
    marginBottom: 12,
    textAlign: 'center',
  },
});
